// PRCTASection.js
import React from 'react';
import { motion } from 'framer-motion';
import { Calendar, MessageCircle, FileText } from 'lucide-react';
import { CALENDLY_PR_CANADA } from '../../../constants';

const PRCTASection = () => {
  const handleBookConsultation = () => {
    window.open(CALENDLY_PR_CANADA, '_blank');
  };

  const ctaOptions = [
    {
      icon: Calendar,
      title: 'Book a Consultation',
      description: 'Schedule a free consultation to discuss your PR pathway and eligibility',
      action: 'Book Now',
      onClick: handleBookConsultation
    },
    {
      icon: MessageCircle,
      title: 'Contact Us',
      description: 'Have questions? Reach out to our team for more information',
      action: 'Get in Touch',
      link: '/contact'
    },
    {
      icon: FileText,
      title: 'View Pricing',
      description: 'See detailed pricing for our PR application support services',
      action: 'View Pricing',
      link: '/pricing'
    }
  ];

  return (
    <section className="py-16 bg-gradient-to-br from-[#202356] to-[#2542D4]">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Main CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl md:text-4xl font-semibold text-white mb-4">
            Ready to Start Your PR Journey?
          </h2>
          <p className="text-white/90 text-base font-light max-w-2xl mx-auto mb-8">
            Let our experienced team guide you through every step of your Canadian permanent residency application.
          </p>
          <button
            onClick={handleBookConsultation}
            className="bg-white hover:bg-gray-100 text-[#202356] font-normal text-base px-8 py-4 rounded-lg transition-colors inline-flex items-center gap-2"
          >
            <Calendar size={20} />
            Book Your Free PR Consultation
          </button>
        </motion.div>

        {/* CTA Options Grid */}
        <div className="grid md:grid-cols-3 gap-6 mt-12">
          {ctaOptions.map((option, index) => {
            const Icon = option.icon;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white/10 backdrop-blur-sm rounded-lg p-6 hover:bg-white/20 transition-colors"
              >
                <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center mb-4">
                  <Icon size={24} className="text-[#202356]" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">
                  {option.title}
                </h3>
                <p className="text-white/80 text-sm font-light leading-relaxed mb-4">
                  {option.description}
                </p>
                {option.onClick ? (
                  <button
                    onClick={option.onClick}
                    className="text-white text-sm font-normal hover:underline"
                  >
                    {option.action} →
                  </button>
                ) : (
                  <a
                    href={option.link}
                    className="text-white text-sm font-normal hover:underline"
                  >
                    {option.action} →
                  </a>
                )}
              </motion.div>
            );
          })}
        </div>

        {/* Trust Indicators */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="mt-12 grid md:grid-cols-3 gap-6 text-center"
        >
          <div className="text-white">
            <p className="text-3xl font-semibold mb-2">98%</p>
            <p className="text-white/80 text-sm font-light">Success Rate</p>
          </div>
          <div className="text-white">
            <p className="text-3xl font-semibold mb-2">500+</p>
            <p className="text-white/80 text-sm font-light">Applications Processed</p>
          </div>
          <div className="text-white">
            <p className="text-3xl font-semibold mb-2">6-24</p>
            <p className="text-white/80 text-sm font-light">Months Processing Time</p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default PRCTASection;
